import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  useRef,
  TouchEvent,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  SelectAssistantCandidateResponse,
  AssistantCandidateProfile,
} from '@dreamai/types';
import api from '../../../api';
import { ChooseProfileStepProps } from '../OnboardingFlow';
import DreamieTransition, {
  DreamieTransitionHandle,
} from './DreamieTransition';
import './choose-profile-step.scss';

/** Preview data returned instantly from the pool (no LLM) */
interface PreviewCard {
  poolEntryId: string;
  nickname: string;
  imageUrl: string;
  predominantColour: string | null;
  gender: string;
}

/** Enriched candidate with intro messages + candidate ID (from LLM enrichment) */
interface CandidateCard {
  poolEntryId: string;
  candidateId: string;
  profile: AssistantCandidateProfile;
  imageUrl: string;
  predominantColour: string | null;
  gender: string;
}

/** Derive chat-bubble border + gradient from the candidate's predominant colour */
function bubbleColors(hex: string | null): {
  border: string;
  from: string;
  to: string;
} {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return { border: '#c1c5cd', from: '#f5f3ef', to: '#faf9f7' };
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number, w: number) => Math.round(c + (255 - c) * w);
  const darken = (c: number) => Math.round(c * 0.45);
  const toHex = (...rgb: number[]) =>
    '#' + rgb.map((c) => c.toString(16).padStart(2, '0')).join('');
  return {
    border: toHex(darken(r), darken(g), darken(b)),
    from: toHex(mix(r, 0.55), mix(g, 0.55), mix(b, 0.55)),
    to: toHex(mix(r, 0.88), mix(g, 0.88), mix(b, 0.88)),
  };
}

/** Interleave two arrays (F-M-F-M…) */
function interleaveByGender<T extends { gender: string }>(items: T[]): T[] {
  const females = items.filter((x) => x.gender === 'female');
  const males = items.filter((x) => x.gender !== 'female');
  const out: T[] = [];
  let fi = 0;
  let mi = 0;
  let nextFemale = true;
  while (fi < females.length || mi < males.length) {
    if (nextFemale && fi < females.length) {
      out.push(females[fi++]);
    } else if (!nextFemale && mi < males.length) {
      out.push(males[mi++]);
    } else if (fi < females.length) {
      out.push(females[fi++]);
    } else {
      out.push(males[mi++]);
    }
    nextFemale = !nextFemale;
  }
  return out;
}

const ChevronLeft = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M15 18l-6-6 6-6' />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M9 6l6 6-6 6' />
  </svg>
);

const ArrowCircleRight = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 8l4 4-4 4M8 12h8' />
  </svg>
);

const ChooseProfileStep: FC<ChooseProfileStepProps> = ({
  onGoBack,
  refreshStatus,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  // Phase 1: fast preview from pool
  const [previews, setPreviews] = useState<PreviewCard[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(true);

  // Phase 2: slow LLM enrichment
  const [candidates, setCandidates] = useState<CandidateCard[]>([]);
  const [enriching, setEnriching] = useState(false);

  // Carousel & UI state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleDots, setVisibleDots] = useState(2);
  const [view, setView] = useState<'carousel' | 'getStarted'>('carousel');
  const [chosenCandidate, setChosenCandidate] = useState<CandidateCard | null>(
    null,
  );
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const touchStartRef = useRef<number>(0);

  // DreamieTransition for initial load animation (first candidate only)
  const transitionRef = useRef<DreamieTransitionHandle>(null);
  const [initialReformDone, setInitialReformDone] = useState(false);
  const holdStartedRef = useRef(false);

  const getPersonality = () => {
    const stored = sessionStorage.getItem('onboarding_personality');
    return stored
      ? JSON.parse(stored)
      : { communicationStyles: ['professional'], energyLevels: ['calm'] };
  };

  // ── Phase 1: fetch previews (instant) then kick off Phase 2 ───────
  const loadData = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const personality = getPersonality();
      const headers = { Authorization: `Bearer ${token}` };

      // Fast: get image URLs + names from pool
      const previewRes = await api.post<{ previews: PreviewCard[] }>(
        '/assistant-onboarding/candidates/preview',
        {
          energyLevels: personality.energyLevels,
          agentType: 'AI Secretary',
        },
        { headers },
      );

      const raw = previewRes.data.previews || [];
      const ordered = interleaveByGender(raw);
      setPreviews(ordered);
      setVisibleDots(Math.min(2, ordered.length));
      setLoadingPreview(false);

      if (ordered.length === 0) return;

      // Slow: enrich with LLM-generated intro messages
      setEnriching(true);
      const enrichRes = await api.post<{ candidates: CandidateCard[] }>(
        '/assistant-onboarding/candidates/enrich',
        {
          poolEntryIds: ordered.map((p) => p.poolEntryId),
          communicationStyles: personality.communicationStyles,
          energyLevels: personality.energyLevels,
          agentType: 'AI Secretary',
        },
        { headers },
      );

      // Re-order enriched candidates to match preview order
      const enriched = enrichRes.data.candidates || [];
      const byPool = new Map(enriched.map((c) => [c.poolEntryId, c]));
      const orderedCandidates = ordered
        .map((p) => byPool.get(p.poolEntryId))
        .filter(Boolean) as CandidateCard[];

      setCandidates(orderedCandidates);
      setEnriching(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          'Failed to load a secretary. Please try again.',
      );
      setLoadingPreview(false);
      setEnriching(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!loadedRef.current) {
      loadedRef.current = true;
      loadData();
    }
  }, [loadData]);

  // ── DreamieTransition: start hold when preview arrives ────────────
  const currentPreview = previews[currentIndex];

  useEffect(() => {
    if (!currentPreview || holdStartedRef.current) return;
    // Start the particle hold animation for the first candidate
    const timer = setTimeout(() => {
      if (!holdStartedRef.current) {
        holdStartedRef.current = true;
        transitionRef.current?.startInHold();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [currentPreview]);

  // ── DreamieTransition: reform when enrichment completes ───────────
  useEffect(() => {
    if (!holdStartedRef.current || initialReformDone) return;
    if (candidates.length === 0) return;
    // Enrichment arrived — reform particles into the first candidate's image
    const first = candidates[0];
    if (first) {
      transitionRef.current?.reformTo(first.imageUrl);
    }
  }, [candidates, initialReformDone]);

  const handleReformComplete = useCallback(() => {
    setInitialReformDone(true);
  }, []);

  // ── Carousel navigation ──────────────────────────────────────────
  const totalCount = candidates.length || previews.length;
  const canNavigate = initialReformDone && candidates.length > 0;

  const goLeft = () => {
    if (canNavigate && currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const goRight = () => {
    if (canNavigate && currentIndex < totalCount - 1) {
      const next = currentIndex + 1;
      setCurrentIndex(next);
      setVisibleDots((prev) => Math.max(prev, next + 1));
    }
  };

  // ── Swipe handling ───────────────────────────────────────────────
  const handleTouchStart = (e: TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!canNavigate) return;
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < totalCount - 1) {
        goRight();
      } else if (diff < 0 && currentIndex > 0) {
        goLeft();
      }
    }
  };

  // ── Choose → Get Started transition ──────────────────────────────
  const handleChoose = (candidate: CandidateCard) => {
    setChosenCandidate(candidate);
    setView('getStarted');
  };

  const handleBackFromGetStarted = () => {
    setView('carousel');
  };

  /** Confirm selection — calls select API and advances onboarding */
  const handleGetStarted = useCallback(async () => {
    if (!chosenCandidate) return;
    setSelecting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const selectRes = await api.post<SelectAssistantCandidateResponse>(
        `/assistant-onboarding/candidate/${chosenCandidate.candidateId}/select`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      sessionStorage.setItem(
        'onboarding_candidate',
        JSON.stringify({
          candidateId: chosenCandidate.candidateId,
          agentId: selectRes.data.agentId,
          profile: chosenCandidate.profile,
          imageUrl: selectRes.data.imageUrl || null,
          imageReady: selectRes.data.imageReady,
          predominantColour: chosenCandidate.predominantColour || null,
        }),
      );

      await refreshStatus();
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          'Failed to select secretary. Please try again.',
      );
    } finally {
      setSelecting(false);
    }
  }, [chosenCandidate, getAccessTokenSilently, refreshStatus]);

  // Current enriched candidate (available after enrichment)
  const currentCandidate = candidates[currentIndex] || null;

  // ── Get Started view ─────────────────────────────────────────────
  if (view === 'getStarted' && chosenCandidate) {
    return (
      <div className='onboarding-step choose-profile-step get-started-view'>
        <div className='step-header'>
          <button className='back-btn' onClick={handleBackFromGetStarted}>
            <ChevronLeft />
          </button>
          <h1>Get started</h1>
        </div>

        <div className='step-inner'>
          <div className='get-started-text'>
            <p className='gs-line1'>You're ready to meet your secretary.</p>
            <p className='gs-line2'>Choose 'Get Started' to begin.</p>
          </div>

          <div className='get-started-image'>
            <img
              src={chosenCandidate.imageUrl}
              alt={chosenCandidate.profile.nickname}
            />
          </div>

          <div className='get-started-footer'>
            <button
              className='step-button primary-gradient'
              onClick={handleGetStarted}
              disabled={selecting}
            >
              <span>{selecting ? 'Setting up…' : 'Get started'}</span>
              {!selecting && <ArrowCircleRight />}
            </button>
          </div>
        </div>

        {error && <p className='generate-error'>{error}</p>}
      </div>
    );
  }

  // ── Carousel view ────────────────────────────────────────────────
  // Determine what to display for the current index
  const displayNickname = currentCandidate
    ? currentCandidate.profile.nickname
    : currentPreview?.nickname || '';
  const displayImageUrl = currentCandidate
    ? currentCandidate.imageUrl
    : currentPreview?.imageUrl || '';
  const displayColour = currentCandidate
    ? currentCandidate.predominantColour
    : currentPreview?.predominantColour || null;

  // Show the initial hold animation only for index 0 before enrichment completes
  const showTransitionAnimation =
    currentIndex === 0 && !initialReformDone && !!currentPreview;

  // Show image + messages for enriched candidates after initial reform
  const showEnrichedContent = initialReformDone && !!currentCandidate;

  return (
    <div
      className='onboarding-step choose-profile-step'
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className='step-header'>
        <button className='back-btn' onClick={onGoBack}>
          <ChevronLeft />
        </button>
        <h1>Choose a profile</h1>
      </div>

      <div className='step-inner'>
        <p className='profile-subtitle'>
          I'm almost ready. Who would you rather work with?
        </p>

        {loadingPreview ? (
          <div className='carousel-loading'>
            <div className='loading-skeleton-row'>
              <div className='skeleton-image' />
              <div className='skeleton-bubbles'>
                <div className='skeleton-bubble' />
                <div className='skeleton-bubble wide' />
                <div className='skeleton-bubble' />
              </div>
            </div>
            <p className='loading-text'>Getting your secretary ready…</p>
          </div>
        ) : currentPreview ? (
          <>
            {/* Carousel nav: always rendered to reserve space, hidden until reform */}
            <div
              className='carousel-nav'
              style={
                initialReformDone
                  ? undefined
                  : { visibility: 'hidden', pointerEvents: 'none' }
              }
            >
              <button
                className={`nav-arrow ${currentIndex === 0 ? 'disabled' : ''}`}
                onClick={goLeft}
                disabled={currentIndex === 0}
              >
                <ChevronLeft />
              </button>

              <div className='carousel-center'>
                <p className='candidate-name'>{displayNickname}</p>
                <div className='page-dots'>
                  {Array.from({
                    length: Math.min(visibleDots, totalCount),
                  }).map((_, i) => (
                    <span
                      key={i}
                      className={`dot ${i === currentIndex ? 'active' : ''}`}
                    />
                  ))}
                </div>
              </div>

              <button
                className={`nav-arrow ${currentIndex >= totalCount - 1 ? 'disabled' : ''}`}
                onClick={goRight}
                disabled={currentIndex >= totalCount - 1}
              >
                <ChevronRight />
              </button>
            </div>

            {/* Candidate display: image + chat bubbles / skeletons */}
            <div className='candidate-display'>
              <div className='candidate-image'>
                {showTransitionAnimation ? (
                  <DreamieTransition
                    ref={transitionRef}
                    imageUrl={currentPreview.imageUrl}
                    alt={currentPreview.nickname}
                    onReformComplete={handleReformComplete}
                    width={213}
                    height={320}
                    startHidden
                  />
                ) : (
                  <img
                    key={currentIndex}
                    src={displayImageUrl}
                    alt={displayNickname}
                  />
                )}
              </div>
              {showEnrichedContent ? (
                <div className='candidate-chats'>
                  {(currentCandidate.profile.introMessages || []).map(
                    (msg, i) => {
                      const colors = bubbleColors(displayColour);
                      return (
                        <div
                          key={i}
                          className='chat-bubble'
                          style={{
                            borderColor: colors.border,
                            background: `linear-gradient(90deg, ${colors.from} 0%, ${colors.to} 100%)`,
                          }}
                        >
                          <p>{msg}</p>
                        </div>
                      );
                    },
                  )}
                </div>
              ) : (
                <div className='candidate-chats'>
                  <div className='chat-bubble skeleton' />
                  <div className='chat-bubble skeleton wide' />
                  <div className='chat-bubble skeleton' />
                </div>
              )}
            </div>

            {/* Choose button + hint: always rendered to reserve space */}
            <div
              className='choose-footer'
              style={
                initialReformDone
                  ? undefined
                  : { visibility: 'hidden', pointerEvents: 'none' }
              }
            >
              <button
                className='step-button primary-gradient'
                onClick={() =>
                  currentCandidate && handleChoose(currentCandidate)
                }
                disabled={!currentCandidate}
              >
                <span>Choose {displayNickname}</span>
                <ArrowCircleRight />
              </button>
              <p className='choose-hint'>
                You'll be able to change their appearance later.
              </p>
            </div>
          </>
        ) : (
          <p className='generate-error'>
            No secretaries available. Please go back and try again.
          </p>
        )}
      </div>

      {error && <p className='generate-error'>{error}</p>}
    </div>
  );
};

export default ChooseProfileStep;
